export interface CitySection {
  heading: string;
  body: string;
}

export interface CityContentEntry {
  name: string;
  region: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  highlights?: string[];
  sections: CitySection[];
  popularServices?: string[];
}

export const CITY_CONTENT: Record<string, CityContentEntry> = {
  casablanca: {
    name: 'Casablanca',
    region: 'Casablanca-Settat',
    metaTitle: 'Artisans et prestataires à Casablanca – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Casablanca : plombier, électricien, peintre, nettoyage, déménagement et plus. Devis gratuit, professionnels vérifiés sur Bricolat.',
    intro:
      'Casablanca, capitale économique du Maroc, est la ville la plus peuplée du pays avec plus de 4 millions d\'habitants. Bricolat vous connecte avec les meilleurs artisans et prestataires de services à domicile à Casablanca et dans sa région. Que vous habitiez Maarif, Anfa, Ain Chock, Hay Mohammadi ou Sidi Moumen, trouvez rapidement un professionnel qualifié près de chez vous.',
    highlights: [
      'Plus de 100 prestataires vérifiés à Casablanca',
      'Intervention dans tous les quartiers : Maarif, Anfa, Hay Hassani, Ain Sebaa, Ben Msik…',
      'Devis gratuit sous 24h',
    ],
    sections: [
      {
        heading: 'Pourquoi Bricolat à Casablanca ?',
        body: `Casablanca est une métropole en constante évolution où la demande de services à domicile est très forte. Les besoins en rénovation, en entretien et en dépannage y sont élevés, mais trouver un artisan de confiance reste souvent difficile.

Bricolat simplifie cette recherche en réunissant des centaines de prestataires casablancais vérifiés : leur identité est confirmée, leurs avis sont authentiques, et leur profil professionnel est consultable avant tout engagement.

Nos artisans couvrent tous les quartiers de Casablanca, du centre-ville aux zones périphériques comme Bouskoura, Dar Bouazza, Mohammedia et Nouaceur.`,
      },
      {
        heading: 'Services les plus demandés à Casablanca',
        body: `À Casablanca, les services les plus sollicités sur Bricolat sont :

Plomberie et sanitaire : fuites, canalisations bouchées, installation de chauffe-eau.
Électricité : dépannage, tableau électrique, installation de prises.
Peinture : rénovation d'appartements et de bureaux.
Nettoyage professionnel : ménage régulier, nettoyage de fin de chantier.
Climatisation : installation et entretien dans un contexte de chaleur estivale.
Déménagement : très forte demande liée au marché immobilier dynamique.`,
      },
      {
        heading: 'Quartiers desservis à Casablanca',
        body: `Nos prestataires interviennent dans l'ensemble des arrondissements et quartiers de Casablanca :

Maarif, Gauthier, Anfa, Californie, Val Fleuri, Bourgogne, Racine, Ain Diab, Ain Chock, Hay Mohammadi, Sbata, Ben Msik, Moulay Rachid, Sidi Moumen, Hay Hassani, Oulfa, Sidi Maarouf, Hay Nahda, Ain Sebaa, Sidi Bernoussi, Aïn Harrouda, et les communes avoisinantes (Bouskoura, Dar Bouazza, Mohammédia, Nouaceur).`,
      },
    ],
    popularServices: ['plomberie', 'electricite', 'peinture', 'nettoyage', 'climatisation', 'demenagement'],
  },

  rabat: {
    name: 'Rabat',
    region: 'Rabat-Salé-Kénitra',
    metaTitle: 'Artisans et prestataires à Rabat – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Rabat : plombier, électricien, peintre, nettoyage. Devis gratuit, prestataires vérifiés. Intervention dans tous les quartiers.',
    intro:
      'Rabat, capitale administrative du Royaume du Maroc, est une ville moderne et bien organisée où la demande en services à domicile est en forte croissance. Bricolat vous met en relation avec des artisans et prestataires qualifiés dans tous les quartiers de Rabat, Salé, Temara et Skhirat.',
    highlights: [
      'Couverture de Rabat, Salé, Temara et Skhirat',
      'Devis gratuit sous 24h',
      'Artisans vérifiés avec avis authentiques',
    ],
    sections: [
      {
        heading: 'Services à domicile à Rabat',
        body: `Que vous résidiez dans les quartiers huppés d'Agdal, Hassan ou Hay Riad, ou dans des quartiers plus denses comme Yacoub El Mansour, Hay Salam ou Akkari, Bricolat vous connecte avec des prestataires locaux rapidement disponibles.

La capitale administrative a un tissu de logements très varié : villas des ambassadeurs, appartements modernes à Agdal et Hay Riad, riads dans la médina et quartiers mixtes. Nos artisans s'adaptent à tous les types de logements et de projets.`,
      },
      {
        heading: 'Quartiers de Rabat couverts',
        body: `Agdal, Hay Riad, Hassan, Souissi, Aviation, Orangers, Diour Jamaa, Yacoub El Mansour, Hay Salam, Akkari, Takaddoum, Médina, Kasbah des Oudaias. Et à Salé : Bettana, Hay Salam, Tabriquet, Layayda. Temara et Skhirat sont également couvertes.`,
      },
    ],
    popularServices: ['plomberie', 'electricite', 'peinture', 'nettoyage', 'jardinage', 'menuiserie'],
  },

  marrakech: {
    name: 'Marrakech',
    region: 'Marrakech-Safi',
    metaTitle: 'Artisans et prestataires à Marrakech – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Marrakech : plombier, électricien, peintre, climatisation, jardinage. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Marrakech, la ville ocre, est l\'une des destinations touristiques les plus prisées du monde et un marché immobilier très dynamique. Bricolat vous connecte avec des artisans et prestataires de confiance pour l\'entretien de votre riad, villa ou appartement à Marrakech.',
    highlights: [
      'Spécialistes riads et villas de luxe',
      'Climatisation et jardinage : forte demande liée au climat',
      'Couverture de Guéliz, Hivernage, Médina, Palmeraie, Mellah',
    ],
    sections: [
      {
        heading: 'Services les plus demandés à Marrakech',
        body: `Le parc immobilier de Marrakech est très hétérogène : riads de la médina, villas modernes en Palmeraie, appartements dans Guéliz et Hivernage. Les besoins en services à domicile sont en conséquence très variés.

Les demandes les plus fréquentes sont :
Climatisation : essentielle dans un climat où les températures estivales dépassent régulièrement 40°C.
Jardinage et arrosage automatique : entretien des jardins, palmeraies et cours intérieures.
Plomberie : entretien des fontaines, hammams et systèmes d'arrosage.
Peinture et décoration : rénovation de riads, tadelakt, stuc marocain.
Menuiserie artisanale : boiseries, portes sculptées, plafonds en bois.

Nos artisans connaissent les spécificités du bâti marrakchi et des techniques artisanales locales.`,
      },
      {
        heading: 'Quartiers desservis à Marrakech',
        body: `Médina (Derb Dabachi, Bab Doukkala, Derb Snane), Guéliz, Hivernage, Palmeraie, Mellah, Sidi Youssef Ben Ali, Route de Casablanca, Semlalia, Agdal, M'hamid, Targa. Et les communes proches : Tamansourt, Ait Ourir.`,
      },
    ],
    popularServices: ['climatisation', 'jardinage', 'plomberie', 'peinture', 'menuiserie', 'nettoyage'],
  },

  tanger: {
    name: 'Tanger',
    region: 'Tanger-Tétouan-Al Hoceima',
    metaTitle: 'Artisans et prestataires à Tanger – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Tanger : plombier, électricien, peintre, menuiserie aluminium. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Tanger, carrefour entre l\'Europe et l\'Afrique, est en plein essor économique avec de nombreux projets immobiliers et industriels. Bricolat vous connecte avec des artisans qualifiés pour tous vos besoins à domicile à Tanger.',
    highlights: [
      'Spécialistes menuiserie aluminium pour le climat côtier',
      'Couverture de Tanger-Médina, Malabata, Boukhalef, Tanja Balia',
      'Devis gratuit sous 24h',
    ],
    sections: [
      {
        heading: 'Spécificités des travaux à Tanger',
        body: `Le climat côtier de Tanger (humidité élevée, vent, embruns) impose des choix de matériaux spécifiques. Nos artisans tangérois connaissent bien ces contraintes :

Menuiserie aluminium et PVC : résistance à la corrosion, idéale pour les façades exposées au vent marin.
Peinture anti-humidité : indispensable pour les façades et les pièces exposées.
Plomberie : protections contre les dépôts calcaires fréquents dans la région.

Tanger bénéficie d'un développement immobilier soutenu. Qu'il s'agisse de la Médina historique, du quartier du California, de Malabata ou des nouvelles zones résidentielles comme Tanja Balia et Al Manzah, nos prestataires couvrent toute la ville.`,
      },
    ],
    popularServices: ['plomberie', 'electricite', 'menuiserie', 'peinture', 'nettoyage', 'climatisation'],
  },

  agadir: {
    name: 'Agadir',
    region: 'Souss-Massa',
    metaTitle: 'Artisans et prestataires à Agadir – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Agadir : plombier, électricien, jardinage, peinture. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Agadir, capitale touristique du Souss, bénéficie d\'un climat méditerranéen exceptionnel toute l\'année. La ville connaît un développement immobilier rapide avec de nombreuses villas, résidences et hôtels. Bricolat vous met en relation avec des artisans qualifiés dans toute la région d\'Agadir.',
    highlights: [
      'Spécialistes jardinage et piscines dans le climat agréable du Souss',
      'Couverture d\'Agadir, Aït Melloul, Inezgane, Dcheira',
      'Artisans vérifiés avec profils détaillés',
    ],
    sections: [
      {
        heading: 'Services populaires à Agadir',
        body: `Agadir est une ville de villégiature où les propriétaires investissent dans la qualité de leur cadre de vie. Les services les plus demandés sont :

Jardinage : entretien de jardins et piscines, gazon, arrosage automatique grâce au climat doux.
Peinture : rénovation de villas, résistance aux UV dans une ville très ensoleillée.
Plomberie : installation et maintenance de systèmes d'arrosage automatique, piscines.
Nettoyage : résidences secondaires et appartements de vacances.

La région d'Agadir bénéficie d'une main d'œuvre qualifiée et d'artisans expérimentés dans la construction et la rénovation de résidences de standing.`,
      },
    ],
    popularServices: ['jardinage', 'plomberie', 'peinture', 'nettoyage', 'electricite', 'climatisation'],
  },

  fes: {
    name: 'Fès',
    region: 'Fès-Meknès',
    metaTitle: 'Artisans et prestataires à Fès – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Fès : plombier, électricien, menuiserie artisanale, rénovation de riad. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Fès, première capitale spirituelle et intellectuelle du Maroc, abrite la plus grande médina médiévale du monde. Bricolat vous connecte avec des artisans qualifiés, notamment spécialisés dans la rénovation patrimoniale, la menuiserie artisanale et l\'artisanat fassi.',
    highlights: [
      'Experts en rénovation de riads et bâti ancien',
      'Artisans spécialisés en zellige, tadelakt et boiseries',
      'Couverture de la Médina, Ville Nouvelle, Aïn Chkef',
    ],
    sections: [
      {
        heading: 'Rénovation patrimoniale à Fès',
        body: `Fès est réputée pour son artisanat d'exception. La rénovation d'un riad à Fès fait appel à des savoir-faire traditionnels : zellige (mosaïque de céramique), tadelakt (enduit de chaux marocain), moucharabieh (boiseries ajourées), plafonds en cèdre sculpté et stuc.

Nos artisans fassis maîtrisent ces techniques ancestrales tout en les combinant avec les exigences modernes (plomberie, électricité, isolation). Ils interviennent aussi bien dans la médina historique (Fès El Bali, Fès El Jdid) que dans la Ville Nouvelle.

Les services les plus demandés à Fès incluent : rénovation et restauration de riads, menuiserie artisanale (portes sculptées, boiseries, plafonds), pose de zellige et carrelage marocain, plomberie pour fontaines et hammams, électricité et éclairage d'ambiance.`,
      },
    ],
    popularServices: ['menuiserie', 'peinture', 'plomberie', 'electricite', 'nettoyage'],
  },

  meknes: {
    name: 'Meknès',
    region: 'Fès-Meknès',
    metaTitle: 'Artisans et prestataires à Meknès – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Meknès : plombier, électricien, peintre, menuiserie. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Meknès, ancienne capitale impériale du Maroc, est une ville à taille humaine en plein développement. Bricolat vous connecte avec des artisans qualifiés pour tous vos besoins à domicile dans la ville et ses environs.',
    highlights: [
      'Prestataires disponibles dans toute la ville',
      'Devis gratuit sous 24h',
      'Experts en rénovation et entretien',
    ],
    sections: [
      {
        heading: 'Services à Meknès',
        body: `Meknès est une ville dynamique où de nombreux projets de rénovation et de construction sont en cours. Nos prestataires couvrent les quartiers de la Médina, Hamria, Nouvelle Médina, Zitoune et les zones nouvelles d'extension.

Les services les plus demandés incluent la plomberie, l'électricité, la peinture et la menuiserie. La ville bénéficie d'artisans expérimentés issus d'une tradition artisanale forte dans la région Fès-Meknès.`,
      },
    ],
    popularServices: ['plomberie', 'electricite', 'peinture', 'menuiserie', 'nettoyage'],
  },

  oujda: {
    name: 'Oujda',
    region: 'Oriental',
    metaTitle: 'Artisans et prestataires à Oujda – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Oujda : plombier, électricien, peintre, déménagement. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Oujda, capitale de la région de l\'Oriental, est la grande métropole de l\'est du Maroc. Bricolat vous connecte avec des prestataires qualifiés à Oujda pour tous vos travaux et services à domicile.',
    highlights: [
      'Couverture d\'Oujda et de la région de l\'Oriental',
      'Prestataires vérifiés avec avis authentiques',
      'Devis gratuit sans engagement',
    ],
    sections: [
      {
        heading: 'Services disponibles à Oujda',
        body: `Oujda et sa région ont connu un développement immobilier important ces dernières années. Nos artisans couvrent les quartiers Hay El Qods, Lazaret, Sidi Yahia, Islane, Ville Nouvelle et les zones périphériques.

Les besoins en plomberie, électricité et peinture sont très fréquents pour l'entretien des logements existants. La demande en déménagement est également forte liée à la mobilité des ménages de la région.`,
      },
    ],
    popularServices: ['plomberie', 'electricite', 'peinture', 'demenagement', 'nettoyage'],
  },

  kenitra: {
    name: 'Kénitra',
    region: 'Rabat-Salé-Kénitra',
    metaTitle: 'Artisans et prestataires à Kénitra – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Kénitra : plombier, électricien, peintre. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Kénitra, ville industrielle et universitaire de la région Rabat-Salé-Kénitra, est en pleine expansion. Bricolat vous connecte avec des artisans qualifiés pour tous vos besoins à domicile à Kénitra et dans ses environs.',
    highlights: [
      'Prestataires dans toute la ville et la région',
      'Devis gratuit sous 24h',
      'Artisans vérifiés et notés',
    ],
    sections: [
      {
        heading: 'Services à Kénitra',
        body: `Kénitra bénéficie de la proximité de Rabat et d'un tissu économique actif. Les besoins en services à domicile sont croissants avec le développement de nouveaux quartiers résidentiels. Plomberie, électricité et peinture sont les prestations les plus demandées.`,
      },
    ],
    popularServices: ['plomberie', 'electricite', 'peinture', 'nettoyage', 'menuiserie'],
  },

  tetouan: {
    name: 'Tétouan',
    region: 'Tanger-Tétouan-Al Hoceima',
    metaTitle: 'Artisans et prestataires à Tétouan – Devis gratuit | Bricolat',
    metaDescription:
      'Trouvez un artisan qualifié à Tétouan : plombier, électricien, peintre, menuiserie. Devis gratuit, prestataires vérifiés sur Bricolat.',
    intro:
      'Tétouan, la "colombe blanche", est une ville andalouse au patrimoine exceptionnel. Bricolat vous connecte avec des artisans qualifiés pour l\'entretien et la rénovation de votre logement à Tétouan.',
    highlights: [
      'Experts en rénovation de bâti andalou',
      'Couverture de Tétouan et du Rif occidental',
      'Prestataires vérifiés',
    ],
    sections: [
      {
        heading: 'Services à Tétouan',
        body: `Tétouan possède une médina classée au patrimoine mondial de l'UNESCO. La rénovation du bâti ancien y fait appel à des savoir-faire spécifiques : enduits à la chaux, boiseries, carrelage traditionnel. Nos artisans couvrent la médina, Martil, M'diq et les zones résidentielles modernes.`,
      },
    ],
    popularServices: ['peinture', 'plomberie', 'electricite', 'menuiserie', 'nettoyage'],
  },
};

export const KNOWN_CITY_SLUGS = Object.keys(CITY_CONTENT);
